(() => {
  const CONTROL_SELECTOR =
    'input:not([type="hidden"]):not([type="button"]):not([type="submit"]), textarea';

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const PHONE_INPUT_ID = 'ContactForm-phone';
  const CONTACT_FORM_ID = 'ContactForm';

  const SUBMIT_ONLY_EMPTY_FIELD_IDS = new Set([
    'ContactForm-firstname',
    'ContactForm-lastname',
    'ContactForm-body',
  ]);

  const BLUR_VALIDATE_FIELD_IDS = new Set(['ContactForm-email', PHONE_INPUT_ID]);

  const getPhoneIndicatif = (phoneControl) => {
    const row = phoneControl.closest('.contact__fields--phone');
    return row?.querySelector('[data-country-code-value]')?.value?.trim() || '+33';
  };

  const buildFullPhoneNumber = (phoneControl) => {
    const raw = phoneControl.value.trim();
    if (!raw) return '';
    const compact = raw.replace(/[\s.\-()]/g, '');
    if (compact.startsWith('+')) return compact;
    const indicatif = getPhoneIndicatif(phoneControl);
    const national = compact.replace(/^0+/, '');
    return `${indicatif}${national}`;
  };

  const isPhoneNumberValid = (phoneControl) => {
    if (typeof libphonenumber === 'undefined') return false;
    const full = buildFullPhoneNumber(phoneControl);
    try {
      if (typeof libphonenumber.isValidPhoneNumber === 'function') {
        return libphonenumber.isValidPhoneNumber(full);
      }
      const parsed = libphonenumber.parsePhoneNumber(full);
      return parsed?.isValid() === true;
    } catch {
      return false;
    }
  };

  const getErrorMessageKind = (issue) => {
    if (issue === 'required') return 'required';
    if (issue === 'format' || issue === 'invalid') return 'invalid';
    return 'fallback';
  };

  const showFieldErrorMessage = (errEl, issue) => {
    const kind = getErrorMessageKind(issue);
    const messageEls = errEl.querySelectorAll('[data-field-error-message]');
    let matched = false;

    messageEls.forEach((el) => {
      const isMatch = el.getAttribute('data-field-error-message') === kind;
      el.hidden = !isMatch;
      if (isMatch) matched = true;
    });

    if (!matched) {
      const fallback = errEl.querySelector('[data-field-error-message="fallback"]');
      if (fallback) {
        messageEls.forEach((el) => {
          el.hidden = true;
        });
        fallback.hidden = false;
        matched = true;
      }
    }

    return matched;
  };

  const isValidatableControl = (el) =>
    el?.matches?.(CONTROL_SELECTOR) && el.willValidate;

  const isContactForm = (form) => form?.id === CONTACT_FORM_ID;

  const initFormFieldValidation = (form) => {
    if (!form || form.dataset.fieldValidationInit === 'true') return;
    form.dataset.fieldValidationInit = 'true';
    form.setAttribute('novalidate', '');

    const contactForm = isContactForm(form);

    const getFieldParts = (control) => {
      if (!control?.id) return null;
      const field = control.closest('.field');
      if (!field) return null;
      const errEl = document.getElementById(`${control.id}-error`);
      if (!errEl?.hasAttribute('data-field-error')) return null;
      if (!control.willValidate) return null;
      return { field, control, errEl };
    };

    const getEmailFormatIssue = (control) => {
      const value = control.value.trim();
      if (!value) return null;
      control.setCustomValidity('');
      if (control.validity.typeMismatch || !EMAIL_REGEX.test(value)) {
        control.setCustomValidity('invalid');
        return 'format';
      }
      return null;
    };

    const getEmailValidationIssue = (control) => {
      const value = control.value.trim();
      if (!value) return 'required';
      return getEmailFormatIssue(control);
    };

    const getPhoneFormatIssue = (control) => {
      const value = control.value.trim();
      if (!value) return null;
      control.setCustomValidity('');
      if (!isPhoneNumberValid(control)) {
        control.setCustomValidity('invalid');
        return 'format';
      }
      return null;
    };

    const getPhoneValidationIssue = (control) => {
      const value = control.value.trim();
      if (!value) return 'required';
      return getPhoneFormatIssue(control);
    };

    const getSubmitOnlyEmptyIssue = (control) => {
      control.setCustomValidity('');
      return control.value.trim() ? null : 'required';
    };

    const getContactSubmitDisplayIssue = (control) => {
      if (SUBMIT_ONLY_EMPTY_FIELD_IDS.has(control.id)) {
        return getSubmitOnlyEmptyIssue(control);
      }
      if (control.type === 'email') {
        return getEmailValidationIssue(control);
      }
      if (control.id === PHONE_INPUT_ID) {
        return getPhoneFormatIssue(control);
      }
      control.setCustomValidity('');
      return control.validity.valid ? null : 'invalid';
    };

    const getContactSubmitBlockIssue = (control) => {
      if (SUBMIT_ONLY_EMPTY_FIELD_IDS.has(control.id)) {
        return getSubmitOnlyEmptyIssue(control);
      }
      if (control.type === 'email') {
        return getEmailValidationIssue(control);
      }
      if (control.id === PHONE_INPUT_ID) {
        return getPhoneFormatIssue(control);
      }
      control.setCustomValidity('');
      return control.validity.valid ? null : 'invalid';
    };

    const getContactBlurDisplayIssue = (control) => {
      if (!BLUR_VALIDATE_FIELD_IDS.has(control.id)) return null;
      if (control.type === 'email') return getEmailFormatIssue(control);
      if (control.id === PHONE_INPUT_ID) return getPhoneFormatIssue(control);
      return null;
    };

    const getValidationIssue = (control) => {
      if (control.type === 'email') return getEmailValidationIssue(control);
      if (control.id === PHONE_INPUT_ID) return getPhoneValidationIssue(control);
      control.setCustomValidity('');
      return control.validity.valid ? null : 'invalid';
    };

    const setFieldError = (control, issue) => {
      const parts = getFieldParts(control);
      if (!parts) return;
      const { field, control: input, errEl } = parts;
      const textEl = errEl.querySelector('[data-field-error-text]');
      if (!issue) {
        field.classList.remove('field--with-error');
        errEl.hidden = true;
        if (textEl) textEl.hidden = true;
        errEl.querySelectorAll('[data-field-error-message]').forEach((el) => {
          el.hidden = true;
        });
        input.removeAttribute('aria-invalid');
        if (input.getAttribute('aria-describedby') === `${input.id}-error`) {
          input.removeAttribute('aria-describedby');
        }
        return;
      }
      const matched = showFieldErrorMessage(errEl, issue);
      if (textEl) textEl.hidden = !matched;
      field.classList.add('field--with-error');
      errEl.hidden = !matched;
      if (!matched) return;
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', `${input.id}-error`);
    };

    const syncSingleField = (control) => {
      const issue = getValidationIssue(control);
      setFieldError(control, issue);
    };

    const clearFieldError = (control) => {
      control.setCustomValidity('');
      setFieldError(control, false);
    };

    const prepareFormValidation = () => {
      form.querySelectorAll(CONTROL_SELECTOR).forEach((control) => {
        if (isValidatableControl(control)) getValidationIssue(control);
      });
    };

    const syncAllFieldErrors = () => {
      form.querySelectorAll('.field').forEach((field) => {
        const control = field.querySelector(CONTROL_SELECTOR);
        if (!control) return;
        if (contactForm) {
          setFieldError(control, getContactSubmitDisplayIssue(control));
          return;
        }
        syncSingleField(control);
      });
    };

    const validateContactFormOnSubmit = () => {
      let firstInvalid = null;

      form.querySelectorAll(CONTROL_SELECTOR).forEach((control) => {
        if (!isValidatableControl(control)) return;
        const displayIssue = getContactSubmitDisplayIssue(control);
        const blockIssue = getContactSubmitBlockIssue(control);
        setFieldError(control, displayIssue);
        if (blockIssue && !firstInvalid) firstInvalid = control;
      });

      firstInvalid?.focus();
      return Boolean(firstInvalid);
    };

    const blockIfInvalid = (event) => {
      if (window.contactFormSecurity?.blockSubmitIfInsecure?.(form, event)) return true;

      if (contactForm) {
        return validateContactFormOnSubmit();
      }

      prepareFormValidation();
      if (form.checkValidity()) return false;
      syncAllFieldErrors();
      form.querySelector('input:invalid, textarea:invalid')?.focus();
      return true;
    };

    form.addEventListener(
      'click',
      (e) => {
        const submitter = e.target.closest?.('button[type="submit"], input[type="submit"]');
        if (!submitter || !form.contains(submitter)) return;
        if (blockIfInvalid(e)) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
        }
      },
      true
    );

    form.addEventListener(
      'submit',
      (e) => {
        if (blockIfInvalid(e)) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
        }
      },
      true
    );

    const protoSubmit = HTMLFormElement.prototype.submit;
    const protoRequestSubmit = HTMLFormElement.prototype.requestSubmit;

    form.submit = function patchedSubmit() {
      if (blockIfInvalid()) return;
      protoSubmit.call(this);
    };

    if (typeof protoRequestSubmit === 'function') {
      form.requestSubmit = function patchedRequestSubmit(submitter) {
        if (blockIfInvalid()) return;
        protoRequestSubmit.call(this, submitter);
      };
    }

    window.contactFormSecurity?.init?.(form);

    form.addEventListener(
      'focusout',
      (e) => {
        const control = e.target;
        if (!isValidatableControl(control)) return;

        if (contactForm) {
          if (SUBMIT_ONLY_EMPTY_FIELD_IDS.has(control.id)) return;
          if (BLUR_VALIDATE_FIELD_IDS.has(control.id)) {
            setFieldError(control, getContactBlurDisplayIssue(control));
          }
          return;
        }

        syncSingleField(control);
      },
      true
    );

    form.addEventListener(
      'input',
      (e) => {
        const control = e.target;
        if (!isValidatableControl(control)) return;
        clearFieldError(control);
      },
      true
    );

    if (contactForm) {
      const params = new URLSearchParams(window.location.search);
      const returnedFromContactSubmit =
        params.get('form_type') === 'contact' ||
        [...params.keys()].some((key) => key.startsWith('contact['));
      if (returnedFromContactSubmit) {
        requestAnimationFrame(() => syncAllFieldErrors());
      }
    }
  };

  const formsToInit = new Set();
  document.querySelectorAll('form[data-field-validation]').forEach((f) => formsToInit.add(f));
  document.querySelectorAll('form[data-field_validation]').forEach((f) => formsToInit.add(f));
  document.querySelectorAll('form[data_field_validation]').forEach((f) => formsToInit.add(f));
  const contactFormById = document.getElementById('ContactForm');
  if (contactFormById) formsToInit.add(contactFormById);
  formsToInit.forEach(initFormFieldValidation);
})();
